import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
// ALLOW-LIST: these are the only protected operations permitted to run while
// password setup is still required, so they intentionally use the generated
// session middleware instead of the password-enforcing layer in
// `@/lib/auth/require-auth`.
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const getPasswordSetupState = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await (context.supabase as any).rpc("current_account_password_ready");
    return { needsPassword: Boolean(error) || data !== true };
  });

/**
 * Sets the password for the signed-in user and flips the `password_set` flag.
 *
 * The password change goes through the user-scoped client so Supabase's own
 * policy (length + leaked-password protection) still applies; the metadata flag
 * is written with the service role so the browser can never forge it.
 */
export const setInitialPassword = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((value: unknown) => z.object({ password: z.string().min(8).max(200) }).parse(value))
  .handler(async ({ data, context }) => {
    const { getRequest } = await import("@tanstack/react-start/server");
    const authHeader = getRequest()?.headers.get("authorization") ?? "";
    const token = authHeader.startsWith("Bearer ") ? authHeader.slice(7).trim() : "";
    if (!token) throw new Error("Unauthorized: No token provided");

    const { supabaseAuthEnv, updatePasswordWithBearer, markPasswordSetForUser } =
      await import("@/lib/password-setup.server");
    const { url, apiKey } = supabaseAuthEnv();
    try {
      // Supabase Auth applies its own password policy here (length + HIBP).
      await updatePasswordWithBearer({ url, apiKey, token, password: data.password });
    } catch (error) {
      // Server-fn errors only carry their message to the browser, so encode the
      // structured Auth failure into a stable, parseable prefix.
      const failure = error as {
        code?: string | null;
        status?: number | null;
        message?: string;
        weak_password?: { reasons?: string[] };
      };
      const reasons = failure.weak_password?.reasons ?? [];
      const code =
        failure.code ?? (reasons.length > 0 ? "weak_password" : "password_update_failed");
      throw Object.assign(
        new Error(`${code}|${failure.status ?? 0}|${reasons.join(",")}|${failure.message ?? ""}`),
        { code, status: failure.status ?? null, weak_password: failure.weak_password },
      );
    }
    // Only flag the account once Auth has actually accepted the new password.
    await markPasswordSetForUser(context.userId, "password_created");
    return { ok: true as const };
  });

/**
 * Flags an account as having a real, user-chosen password — but ONLY after the
 * password itself has been re-proven to the server.
 *
 * This replaces the old `markPasswordSet`, which flipped the flag on the
 * caller's say-so and was therefore a one-call bypass of the whole rule. Here
 * the client must present the actual password, which the server verifies
 * against Supabase Auth before writing anything.
 */
export const confirmPasswordSet = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((value: unknown) => z.object({ password: z.string().min(1).max(200) }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: userData, error: userError } = await context.supabase.auth.getUser();
    const email = userData?.user?.email;
    if (userError || !email) throw new Error("not_authenticated");

    const { verifyPasswordForEmail } = await import("@/lib/password-setup.server");
    const ok = await verifyPasswordForEmail(email, data.password);
    if (!ok) throw new Error("password_verification_failed");

    const { markPasswordSetForUser } = await import("@/lib/password-setup.server");
    await markPasswordSetForUser(context.userId, "password_reproved");
    return { ok: true as const };
  });
